import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ProviderSidebar from "../../provider/components/ProviderSidebar";
import { getProviderProfile, updateProviderProfile } from "@/lib/providerProfile";
import { ApiError } from "@/lib/api";

export default function ProveedorEmpresaPage() {
  const { t } = useTranslation("provider");
  const [company, setCompany] = useState({
    name: "", rfc: "", address: "", phone: "", email: "", website: "", description: "",
  });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProviderProfile()
      .then((p) => {
        if (cancelled) return;
        setCompany({
          name: p.companyName,
          rfc: p.rfc ?? "",
          address: p.location ?? "",
          phone: p.contactPhone ?? "",
          email: p.email,
          website: p.website ?? "",
          description: p.description ?? "",
        });
      })
      // Blanking the form on a failed load used to be silent, which let the provider
      // save empty fields over their real company data. Surface it and block saving.
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!company.name.trim()) {
      setError(t("provider.companyNameRequired"));
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateProviderProfile({
        companyName: company.name.trim(),
        rfc: company.rfc || undefined,
        location: company.address || undefined,
        contactPhone: company.phone || undefined,
        website: company.website || undefined,
        description: company.description || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("provider.profileSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="w-full">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ProviderSidebar />
            <div className="flex-1 min-w-0 space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-xl font-bold text-charcoal">{t("provider.companyProfile")}</h1>
                  {saved && <span className="text-xs font-medium text-ocean bg-ocean/10 px-3 py-1 rounded-full">{t("provider.saved")}</span>}
                </div>
                {loadFailed && (
                  <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mb-6">{t("provider.profileLoadError")}</div>
                )}
                {error && (
                  <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mb-6">{error}</div>
                )}
                {loading ? (
                  <div className="py-10 text-center text-gray-400 text-sm">{t("provider.loading")}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.companyName")}</label>
                        <input type="text" value={company.name} onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">RFC</label>
                        <input type="text" value={company.rfc} onChange={(e) => setCompany((c) => ({ ...c, rfc: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.companyAddress")}</label>
                        <input type="text" value={company.address} onChange={(e) => setCompany((c) => ({ ...c, address: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.phone")}</label>
                        <input type="tel" value={company.phone} onChange={(e) => setCompany((c) => ({ ...c, phone: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.email")}</label>
                        <input type="email" value={company.email} disabled className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.website")}</label>
                        <input type="url" value={company.website} onChange={(e) => setCompany((c) => ({ ...c, website: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("provider.description")}</label>
                        <textarea value={company.description} onChange={(e) => setCompany((c) => ({ ...c, description: e.target.value }))} rows={4} maxLength={2000} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean resize-none" />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-6">
                      <button onClick={handleSave} disabled={saving || loadFailed} className="bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-ocean/90 transition-colors disabled:opacity-50">{saving ? t("provider.saving") : t("provider.save")}</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
